import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';

/**
 * Catálogos controlados — regra 10 do Padrão Kanban IGM.
 *
 * **Backend-first (2026-05-17):** dados vivem em SQLite via `/api/v1/catalogs/:dept/:list`.
 * Sem `localStorage` pra dados de negócio (regra inviolável do checklist).
 *
 * API pública mantida idêntica pra não quebrar chamadores (`useCatalogItems`,
 * `useCatalogActions`, `getCatalogItems`, `resolveCatalogLabel`, etc):
 *   - Hooks usam React Query contra o backend
 *   - Funções diretas (não-hook) leem de um cache em memória que o hook hidrata
 *     na primeira fetch — fallback retorna `[]` (ou o label cru no resolve) se
 *     consultado antes da hidratação
 *
 * Backend seeda os catálogos canônicos via migration 002 — `bootstrapCatalogs()`
 * vira no-op aqui (mantido pra compat de chamadores).
 */

export interface CatalogItem {
  id: string;
  label: string;
  ordem: number;
  ativo: boolean;
  criadoEmIso: string;
}

// ─── Backend types ──────────────────────────────────────────────────────

interface BackendCatalogRow {
  id: string;
  dept: string;
  list: string;
  label: string;
  ordem: number;
  ativo: boolean;
  criadoEmIso: string;
}

function shape(row: BackendCatalogRow): CatalogItem {
  return {
    id: row.id,
    label: row.label,
    ordem: row.ordem,
    ativo: row.ativo,
    criadoEmIso: row.criadoEmIso,
  };
}

// ─── Cache em memória hidratado pelo React Query ────────────────────────

const cache = new Map<string, CatalogItem[]>();

function cacheKey(dept: string, list: string): string {
  return `${dept}|${list}`;
}

function readCache(dept: string, list: string): CatalogItem[] {
  return cache.get(cacheKey(dept, list)) ?? [];
}

function setCache(dept: string, list: string, items: CatalogItem[]): void {
  cache.set(cacheKey(dept, list), items);
}

// ─── Funções diretas (sync, read-only) ─────────────────────────────────

export function getCatalogItems(dept: string, list: string): CatalogItem[] {
  return readCache(dept, list);
}

export function getCatalogItemById(
  dept: string,
  list: string,
  id: string
): CatalogItem | undefined {
  return readCache(dept, list).find((i) => i.id === id);
}

/**
 * Resolve um value (ID OU label) pro label do catálogo. Fallback retorna o
 * próprio value pra que campos não-normalizados ainda exibam algo legível.
 * Sync: usa o cache hidratado pelo `useCatalogItems`. Antes da hidratação,
 * cai no fallback (idOrLabel literal) — render se ajusta quando query resolve.
 */
export function resolveCatalogLabel(
  dept: string,
  list: string,
  idOrLabel: string
): string {
  if (!idOrLabel) return '';
  const byId = getCatalogItemById(dept, list, idOrLabel);
  if (byId) return byId.label;
  const byLabel = readCache(dept, list).find(
    (i) => i.label.trim().toLowerCase() === idOrLabel.trim().toLowerCase()
  );
  if (byLabel) return byLabel.label;
  return idOrLabel;
}

/**
 * Resolve uma string legada (label livre) pra um ID do catálogo quando o
 * cache já tá hidratado. Antes da hidratação (ex: chamado no module init do
 * `comercial-store` pra normalizar MOCK_DEALS), retorna o label como-is — o
 * `resolveCatalogLabel` no render faz a tradução depois.
 *
 * Em backend-first este helper NÃO cria item novo no backend — criação custom
 * passa por `useCatalogActions.createItem` (admin only).
 */
export function ensureLegacyItem(dept: string, list: string, label: string): string {
  const items = readCache(dept, list);
  if (items.length === 0) return label; // cache vazio — pass-through
  const exact = items.find(
    (i) => i.label.trim().toLowerCase() === label.trim().toLowerCase()
  );
  return exact ? exact.id : label;
}

/**
 * No-op em backend-first: catálogos canônicos são seedados no SQLite via
 * `server/db/migrations/002-seed-data.sql`. Função mantida pra compat de
 * chamadores (`comercial-store.bootstrapCatalogs()` continua compilando).
 */
export function bootstrapCatalogs(): void {
  // intentionally empty
}

// ─── Hook reativo (React Query) ────────────────────────────────────────

export function useCatalogItems(dept: string, list: string): CatalogItem[] {
  const { data } = useQuery({
    queryKey: ['v1', 'catalogs', dept, list],
    queryFn: async () => {
      const rows = await api.get<BackendCatalogRow[]>(`/api/v1/catalogs/${dept}/${list}`);
      const items = rows.map(shape);
      setCache(dept, list, items);
      return items;
    },
    enabled: hasToken(),
    staleTime: 60_000,
  });
  return data ?? readCache(dept, list);
}

export interface CreateCatalogItemInput {
  label: string;
}

export function useCatalogActions(dept: string, list: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['v1', 'catalogs', dept, list] });

  const createItem = useCallback(
    async (input: CreateCatalogItemInput): Promise<CatalogItem> => {
      const row = await api.post<BackendCatalogRow>(`/api/v1/catalogs/${dept}/${list}`, {
        label: input.label.trim(),
      });
      const item = shape(row);
      // Otimismo: já mete no cache pra resolveCatalogLabel ver imediatamente
      setCache(dept, list, [...readCache(dept, list), item]);
      await invalidate();
      return item;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, dept, list]
  );

  const updateItem = useCallback(
    async (
      id: string,
      patch: Partial<Pick<CatalogItem, 'label' | 'ativo' | 'ordem'>>
    ): Promise<void> => {
      const body: Record<string, unknown> = {};
      if (patch.label !== undefined) body.label = patch.label.trim();
      if (patch.ativo !== undefined) body.ativo = patch.ativo;
      if (patch.ordem !== undefined) body.ordem = patch.ordem;
      await api.put(`/api/v1/catalogs/${dept}/${list}/${id}`, body);
      await invalidate();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, dept, list]
  );

  const reorderItems = useCallback(
    async (orderedIds: string[]): Promise<void> => {
      await api.put(`/api/v1/catalogs/${dept}/${list}/-/reorder`, { orderedIds });
      await invalidate();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, dept, list]
  );

  const toggleActive = useCallback(
    async (id: string): Promise<void> => {
      const items = readCache(dept, list);
      const current = items.find((i) => i.id === id);
      if (!current) return;
      await api.put(`/api/v1/catalogs/${dept}/${list}/${id}`, { ativo: !current.ativo });
      await invalidate();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, dept, list]
  );

  const deleteItem = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/api/v1/catalogs/${dept}/${list}/${id}`);
      await invalidate();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, dept, list]
  );

  return { createItem, updateItem, reorderItems, toggleActive, deleteItem };
}

// ─── Test helpers ───────────────────────────────────────────────────────

/** SÓ tests — limpa cache em memória. */
export function __resetAllCatalogsForTests(): void {
  cache.clear();
}

/**
 * SÓ tests — popula o cache manualmente. Substitui a hidratação via
 * `useCatalogItems` em testes que não montam React Query.
 */
export function __seedCatalogCacheForTests(
  dept: string,
  list: string,
  items: CatalogItem[]
): void {
  setCache(dept, list, items);
}
